import {useFillSurface} from '../FillSurface';

/**
 * The frames a form control can wear, on screen and on paper.
 *
 * **A typed model rather than a boolean and a class string.** Each entry says
 * what the control IS — a box waiting for an answer, one nobody can type into,
 * a pad waiting for a signature — and what that looks like on each of the two
 * surfaces. The literals used to be written out at each of a dozen call sites
 * and combined with an `inputFrame(forExport, …)` helper whose whole body was
 * `forExport ? '' : frame`; the two `<select>`s did not go through it at all,
 * because a select needs a different answer on paper and the helper had no way
 * to say so. Naming the kinds is what lets every control ask the same question.
 *
 * **The PDF is a document, not a form.** The outline and fill around a text
 * answer, a dropdown or a signature pad are the app telling a crew where to
 * type, and on paper they are boxes drawn round a sentence. Nothing else about
 * the control changes — same element, same padding, same size — so the page
 * still paginates exactly as the screen it was captured from, which is the whole
 * reason the export renders this DOM rather than a second layout built for
 * paper.
 *
 * A checkbox keeps its square, and a signature keeps the dashed rules beneath
 * it: those are the document's own marks. Someone reads a printed checkbox by
 * its box, and signs above a line. Neither appears here.
 */
const CONTROL_FRAME = {
  /** A box waiting for an answer. */
  open: {screen: 'border border-gray-300', print: ''},
  /** A box nobody can type into — a locked value, or a settled document. */
  closed: {screen: 'border border-gray-200 bg-gray-50', print: ''},
  /** A pad waiting for a signature. */
  pad: {screen: 'border border-gray-300 bg-gray-50', print: ''},
  /**
   * A native `<select>`. On paper its own arrow goes with the frame — a browser
   * draws it whatever we do, and it is the app asking a question on a page
   * nobody can answer it on.
   */
  select: {screen: 'border border-gray-300', print: 'appearance-none'},
} as const;

export type ControlFrameKind = keyof typeof CONTROL_FRAME;

/**
 * The frame classes a control wears on THIS surface.
 *
 * Surface-level, not per-slot: the export is a property of the whole canvas, so
 * a renderer reads it once and every control it draws answers the same way.
 */
export function useControlFrame(): (kind: ControlFrameKind) => string {
  const {forExport} = useFillSurface();
  return (kind) => (forExport ? CONTROL_FRAME[kind].print : CONTROL_FRAME[kind].screen);
}
